import React from "react";
import { Box, Stack } from "@mui/material";
import { ParlorDetailProps, ClickEventType } from "../../../types";
import { ColorSystemProps } from "../../../constants";
import { customEvent } from "../../../utils";

export function FTypeBinsoPriceSummary({
  defaultParlor,
  mainColor,
  ctaHref,
  ctaText,
  event,
}: {
  defaultParlor: ParlorDetailProps;
  mainColor: ColorSystemProps;
  ctaHref?: string;
  ctaText?: string;
  event?: ClickEventType;
}) {
  const binsos = [...(defaultParlor.binsos || [])].sort(
    (a, b) => (a.charge || 0) - (b.charge || 0),
  );
  const avg = defaultParlor.binso_average_charge;

  if (!binsos.length) return null;

  const minPy = Math.min(...binsos.map((b) => b.pyeong || 0));
  const maxPy = Math.max(...binsos.map((b) => b.pyeong || 0));

  return (
    <Stack gap="0" padding="56px 20px">
      <Box
        component="h2"
        className="h2"
        style={{
          fontSize: "24px",
          fontWeight: 700,
          marginTop: "14px",
          color: "#252525",
        }}
      >
        빈소 규모와 사용료 한눈에 보기
      </Box>
      <Box
        component="p"
        style={{
          marginTop: "14px",
          fontSize: "16px",
          lineHeight: 1.75,
          color: "#535353",
          maxWidth: "720px",
        }}
      >
        총 {binsos.length}개 빈소({minPy}~{maxPy}평)를 운영하며, 평형과 등급에
        따라 1일 사용료가 달라집니다. 3일장 기준으로 3배를 예상하세요.
      </Box>

      <Box
        component="ul"
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(2, 1fr)",
          "@media (min-width: 640px)": {
            gridTemplateColumns: "repeat(3, 1fr)",
          },
          gap: "12px",
          marginTop: "28px",
          listStyle: "none",
          padding: 0,
        }}
      >
        {binsos.map((b, i) => {
          const isSpecial = (b.item || "").includes("특");
          const type = isSpecial ? "특실" : "일반실";
          const num = String(i + 1).padStart(2, "0");
          return (
            <Box
              key={i}
              component="li"
              style={{
                display: "flex",
                flexDirection: "column",
                background: "#fff",
                border: "1px solid #e9e9e9",
                borderRadius: "14px",
                overflow: "hidden",
              }}
            >
              <Box
                style={{
                  height: "4px",
                  background: isSpecial ? mainColor.c500 : mainColor.c300,
                }}
              />
              <Box style={{ padding: "18px 16px 20px", flex: 1, display: "flex", flexDirection: "column" }}>
                <Box
                  style={{
                    fontSize: "11px",
                    fontWeight: 700,
                    color: mainColor.c500,
                    letterSpacing: "0.08em",
                    marginBottom: "8px",
                  }}
                >
                  {num}
                </Box>
                <Box
                  style={{
                    fontSize: "26px",
                    fontWeight: 900,
                    color: "#252525",
                    letterSpacing: "-0.02em",
                    lineHeight: 1,
                  }}
                >
                  {b.pyeong || 0}
                  <Box
                    component="span"
                    style={{ fontSize: "15px", fontWeight: 600, marginLeft: "2px" }}
                  >
                    평
                  </Box>
                </Box>
                <Box
                  style={{
                    marginTop: "8px",
                    fontSize: "13px",
                    color: "#535353",
                    flex: 1,
                  }}
                >
                  {b.item || "—"}
                </Box>
                <Box
                  style={{
                    display: "inline-block",
                    marginTop: "8px",
                    padding: "3px 8px",
                    borderRadius: "999px",
                    fontSize: "11px",
                    fontWeight: 600,
                    color: isSpecial ? mainColor.c500 : "#8f8f8f",
                    background: isSpecial ? mainColor.c100 : "#f6f4f4",
                    alignSelf: "flex-start",
                  }}
                >
                  {type}
                </Box>
                <Box
                  style={{
                    marginTop: "16px",
                    paddingTop: "14px",
                    borderTop: "1px solid #f0f0f0",
                  }}
                >
                  <Box
                    style={{
                      fontSize: "18px",
                      fontWeight: 800,
                      color: "#252525",
                      fontFeatureSettings: '"tnum" on',
                    }}
                  >
                    {(b.charge || 0).toLocaleString("ko-KR")}원
                  </Box>
                  <Box
                    style={{
                      marginTop: "2px",
                      fontSize: "11px",
                      color: "#8f8f8f",
                      fontWeight: 500,
                    }}
                  >
                    / 1일 기준
                  </Box>
                </Box>
              </Box>
            </Box>
          );
        })}
      </Box>

      {avg ? (
        <Box
          component="p"
          style={{
            marginTop: "16px",
            fontSize: "12px",
            color: "#8f8f8f",
            lineHeight: 1.5,
          }}
        >
          * 빈소 평균 사용료는{" "}
          <strong>{avg.toLocaleString("ko-KR")}원/1일</strong> 수준입니다. 실제
          부과 금액은 빈소 선택과 사용 일수에 따라 달라집니다.
        </Box>
      ) : null}

      {ctaHref && (
        <Box style={{ marginTop: "24px" }}>
          {ctaText && (
            <Box
              style={{
                fontSize: "13px",
                color: "#8f8f8f",
                marginBottom: "10px",
                textAlign: "center",
                whiteSpace: "pre-wrap",
              }}
            >
              {ctaText}
            </Box>
          )}
          <Box
            component="a"
            href={ctaHref}
            onClick={event ? () => customEvent(event) : undefined}
            style={{
              display: "block",
              padding: "16px",
              borderRadius: "12px",
              fontSize: "16px",
              fontWeight: 700,
              background: mainColor.c400,
              color: "#fff",
              textDecoration: "none",
              textAlign: "center",
            }}
          >
            장례 비용 확인하기
          </Box>
          <Box
            component="p"
            style={{
              marginTop: "10px",
              fontSize: "12px",
              color: "#888",
              lineHeight: 1.5,
              textAlign: "center",
            }}
          >
            ※ e하늘장사정보시스템을 기반으로 고이장례연구소에서 예상 값을 산출하여 제공해요
          </Box>
        </Box>
      )}
    </Stack>
  );
}
